import { useTranslation } from 'react-i18next';
import { resolveErrorMessageKey } from '@constants/error-codes/error-code-map';
import { MutationFunction, MutationKey, useMutation, UseMutationResult } from '@tanstack/react-query';
import { RequestError } from '@utils/request/request.type';
import { showToastErrorRequest } from '@utils/show-toast-error-request/show-toast-error-request';

import { useRefreshTokenHandler } from './auth/use-refresh-token-handler';
import { useToast } from './use-toast/use-toast';

export type GenericMutationOptions<TData = unknown, TError = RequestError, TVariables = void, TContext = unknown> = {
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: TError, variables: TVariables) => void;
  onSettled?: (data: TData | undefined, error: TError | null, variables: TVariables) => void;
  onMutate?: (variables: TVariables) => TContext | Promise<TContext>;
  retry?: number | boolean;
  retryDelay?: number | ((attemptIndex: number) => number);
  meta?: Record<string, unknown>;
  gcTime?: number;
  networkMode?: 'online' | 'always' | 'offlineFirst';
  /** Mutations sharing a scope id run in series, so responses cannot land out of order. */
  scope?: { id: string };
};

export const useGenericMutation = <TData = unknown, TError = RequestError, TVariables = void, TContext = unknown>(
  key: MutationKey,
  fn: MutationFunction<TData, TVariables>,
  options?: GenericMutationOptions<TData, TError, TVariables, TContext>,
  hideError?: boolean
): UseMutationResult<TData, TError, TVariables, TContext> & { isLoading: boolean } => {
  const toast = useToast();
  const { t } = useTranslation();

  const { createRefreshAndRetryHandler } = useRefreshTokenHandler();
  const handleRefreshAndRetry = createRefreshAndRetryHandler<TVariables, TData | Promise<TData>>((variables) =>
    fn(variables, undefined as never)
  );

  // MUTATION FUNCTION WITH IMMEDIATE RETRY
  const mutationFnWithRetry: MutationFunction<TData, TVariables> = async (variables, context) => {
    try {
      return await fn(variables, context);
    } catch (error: unknown) {
      if (error instanceof RequestError && error.status === 401) {
        return await handleRefreshAndRetry(variables);
      }
      // If not 401, propagate the error
      throw error;
    }
  };

  const mutation = useMutation<TData, TError, TVariables, TContext>({
    mutationKey: key,
    mutationFn: mutationFnWithRetry,
    ...options,
    onSuccess: (data, variables) => {
      if (options?.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: (error, variables) => {
      // 401 errors are now handled in mutationFnWithRetry, only handle other errors here
      if (options?.onError) {
        options.onError(error, variables);
      }

      if (error instanceof RequestError) {
        if (!hideError && showToastErrorRequest(error)) {
          const messageKey = resolveErrorMessageKey(error.code);
          toast.push({
            title: messageKey ? t(messageKey) : error.message,
            text: '',
            type: 'error',
            disableTimeOut: true,
          });
        }
      }
    },
    onSettled: (data, error, variables) => {
      if (options?.onSettled) {
        options.onSettled(data, error, variables);
      }
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending,
  };
};
